import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Help as HelpIcon,
  BugReport as BugReportIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  ContactSupport as ContactSupportIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import apiService from '../../services/apiService';
import { useTranslation } from 'react-i18next';

const SupportPage: React.FC = () => {
  const { user } = useAuth();
  const { actualMode: mode } = useThemeContext();
  const { t } = useTranslation();

  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: '',
    priority: 'medium' as 'low' | 'medium' | 'high'
  });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const supportOptions = [
    {
      id: 'email',
      title: t('supportPage.options.email.title'),
      icon: <EmailIcon sx={{ fontSize: '2.5rem' }} />,
      description: t('supportPage.options.email.description'),
      color: '#1976D2',
      action: () => handleEmailSupport(),
    },
    {
      id: 'faq',
      title: t('supportPage.options.faq.title'),
      icon: <HelpIcon sx={{ fontSize: '2.5rem' }} />,
      description: t('supportPage.options.faq.description'),
      color: '#2E7D32',
      action: () => window.location.href = '/help',
    },
    {
      id: 'report',
      title: t('supportPage.options.report.title'),
      icon: <BugReportIcon sx={{ fontSize: '2.5rem' }} />,
      description: t('supportPage.options.report.description'),
      color: '#ED6C02',
      action: () => handleReportIssue(),
    },
    {
      id: 'safety',
      title: t('supportPage.options.safety.title'),
      icon: <SecurityIcon sx={{ fontSize: '2.5rem' }} />,
      description: t('supportPage.options.safety.description'),
      color: '#D32F2F',
      action: () => handleSafetyConcern(),
      priority: true,
    },
  ];

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: '2rem' }} />,
      title: t('supportPage.contactInfo.email'),
      value: 'support@rideclub.ca',
      description: t('supportPage.contactInfo.emailSub'),
      action: () => { window.location.href = 'mailto:support@rideclub.ca'; }
    },
    {
      icon: <SecurityIcon sx={{ fontSize: '2rem' }} />,
      title: t('supportPage.contactInfo.safety'),
      value: 'safety@rideclub.ca',
      description: t('supportPage.contactInfo.safetySub'),
      action: () => { window.location.href = 'mailto:safety@rideclub.ca'; }
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: '2rem' }} />,
      title: t('supportPage.contactInfo.hours'),
      value: t('supportPage.contactInfo.hoursValue'),
      description: t('supportPage.contactInfo.hoursSub'),
    }
  ];

  const handleEmailSupport = () => {
    const subject = 'Ride Club Support Request';
    const body = `
Hi Ride Club Support Team,

User Information:
- Name: ${user?.preferredName || user?.firstName || ''} ${user?.lastName ? user?.lastName.charAt(0) + '.' : ''}
- Email: ${user?.email}
- Phone: ${user?.phoneNumber || 'Not provided'}
- User ID: ${user?.id || 'Unknown'}

Issue Description:
[Please describe your issue here]

Device Information:
- Platform: Web App
- Browser: ${navigator.userAgent}

Thank you for your assistance.

Best regards,
${user?.firstName || 'User'}
    `.trim();

    const emailUrl = `mailto:support@rideclub.ca?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
  };

  const handleReportIssue = () => {
    setSupportForm(prev => ({
      ...prev,
      subject: t('supportPage.options.report.title'),
      priority: 'medium'
    }));
    setShowForm(true);
  };

  const handleSafetyConcern = () => {
    setSupportForm(prev => ({
      ...prev,
      subject: t('supportPage.options.safety.title'),
      priority: 'high'
    }));
    setShowForm(true);
  };

  const handleSubmitForm = async () => {
    if (!supportForm.subject.trim() || !supportForm.message.trim()) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.createSupportTicket({
        subject: supportForm.subject.trim(),
        message: supportForm.message.trim(),
        priority: supportForm.priority.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
      });

      console.log('Support ticket created:', response.data);

      setSubmitSuccess(true);
      setShowForm(false);
      setSupportForm({
        subject: '',
        message: '',
        priority: 'medium'
      });

      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to submit support request:', error);
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    { value: 'low', label: t('supportPage.form.low'), color: '#2E7D32', description: t('supportPage.form.lowDesc') },
    { value: 'medium', label: t('supportPage.form.medium'), color: '#ED6C02', description: t('supportPage.form.mediumDesc') },
    { value: 'high', label: t('supportPage.form.high'), color: '#D32F2F', description: t('supportPage.form.highDesc') },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, minHeight: '100vh', backgroundColor: mode === 'dark' ? '#000' : '#f5f7fa' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 900,
            mb: 6,
            fontSize: { xs: '3rem', md: '4.5rem' },
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
            textAlign: 'center'
          }}
        >
          {t('supportPage.title')}
        </Typography>

        {submitSuccess && (
          <Alert
            severity="success"
            sx={{
              mb: 4,
              borderRadius: '24px',
              p: 3,
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              border: '2px solid rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(10px)',
              '& .MuiAlert-icon': {
                fontSize: '2rem'
              }
            }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              {t('supportPage.submitSuccess')}
            </Typography>
            <Typography variant="h6" fontWeight="medium">
              {t('supportPage.submitSuccessSub')}
            </Typography>
          </Alert>
        )}

        {/* Quick Actions */}
        <Paper
          sx={{
            mb: 5,
            p: { xs: 3, md: 5 },
            borderRadius: '32px',
            background: mode === 'dark' ? 'rgba(30, 30, 30, 0.6)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <Typography
            variant="h4"
            fontWeight="900"
            sx={{
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {t('supportPage.howCanWeHelp')}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {supportOptions.map((option) => (
              <Box key={option.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: '24px',
                    border: '2px solid',
                    borderColor: option.priority
                      ? 'rgba(211, 47, 47, 0.3)'
                      : (mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
                    backgroundColor: option.priority
                      ? (mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(255, 235, 238, 0.5)')
                      : 'transparent',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      borderColor: option.color,
                      backgroundColor: option.color + '05'
                    }
                  }}
                  onClick={option.action}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box
                        sx={{
                          color: option.color,
                          backgroundColor: option.color + '15',
                          borderRadius: '20px',
                          p: 2,
                          display: 'flex',
                          boxShadow: `0 10px 15px -3px ${option.color}30`
                        }}
                      >
                        {option.icon}
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight="900" sx={{
                          color: option.priority ? '#D32F2F' : 'text.primary',
                          mb: 1.5,
                          fontSize: '1.5rem'
                        }}>
                          {option.title}
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '1.1rem' }}>
                          {option.description}
                        </Typography>
                        {option.priority && (
                          <Chip
                            label="PRIORITY"
                            size="medium"
                            color="error"
                            sx={{ mt: 2, fontWeight: 'bold', borderRadius: '12px' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Contact Information */}
        <Paper
          sx={{
            mb: 5,
            p: { xs: 3, md: 5 },
            borderRadius: '32px',
            background: mode === 'dark' ? 'rgba(30, 30, 30, 0.6)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
          }}
        >
          <Typography variant="h4" fontWeight="900" sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            {t('supportPage.contactInfo.title')}
          </Typography>

          <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {contactInfo.map((contact, index) => (
              <Box key={index}>
                <ListItem
                  disablePadding
                  sx={{
                    p: 3,
                    borderRadius: '24px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'primary.main', minWidth: '64px' }}>
                    {contact.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="h5" fontWeight="900" sx={{ mb: 1 }}>
                        {contact.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="h5"
                          color="primary.main"
                          fontWeight="bold"
                          sx={{
                            cursor: contact.action ? 'pointer' : 'default',
                            mb: 0.5,
                            '&:hover': contact.action ? { textDecoration: 'underline' } : {}
                          }}
                          onClick={contact.action}
                        >
                          {contact.value}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                          {contact.description}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < contactInfo.length - 1 && <Divider sx={{ my: 1, opacity: 0.5 }} />}
              </Box>
            ))}
          </List>
        </Paper>

        {/* Support Form Button */}
        <Paper
          sx={{
            mb: 5,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: '32px',
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(79, 70, 229, 0.4)'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h3" fontWeight="900" sx={{ mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              {t('supportPage.sendMessage.title')}
            </Typography>
            <Typography variant="h5" sx={{ mb: 5, opacity: 0.9, maxWidth: '800px', mx: 'auto', fontWeight: 500 }}>
              {t('supportPage.sendMessage.description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SendIcon sx={{ fontSize: '1.5rem' }} />}
                onClick={() => setShowForm(true)}
                sx={{
                  py: 2.5,
                  px: 5,
                  borderRadius: '20px',
                  fontSize: '1.4rem',
                  fontWeight: '900',
                  backgroundColor: 'white',
                  color: '#4F46E5',
                  textTransform: 'none',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                {t('supportPage.sendMessage.button')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContactSupportIcon sx={{ fontSize: '1.5rem' }} />}
                onClick={() => window.location.href = '/my-tickets'}
                sx={{
                  py: 2.5,
                  px: 5,
                  borderRadius: '20px',
                  fontSize: '1.4rem',
                  fontWeight: '900',
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  color: 'white',
                  borderWidth: '2px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderWidth: '2px',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {t('supportPage.sendMessage.viewTickets')}
              </Button>
            </Box>
          </Box>
          {/* Decorative elements */}
          <Box sx={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(0,0,0,0.1)' }} />
        </Paper>

        {/* Emergency Notice */}
        <Alert
          severity="warning"
          sx={{
            mb: 4,
            borderRadius: '24px',
            p: 3,
            backgroundColor: mode === 'dark' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(254, 252, 232, 0.8)',
            border: '2px solid rgba(234, 179, 8, 0.2)',
            '& .MuiAlert-icon': {
              fontSize: '2rem'
            }
          }}
        >
          <Typography variant="h5" fontWeight="900" sx={{ mb: 1 }}>
            {t('supportPage.emergency.title')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
            {t('supportPage.emergency.safety')}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {t('supportPage.emergency.technical')}
          </Typography>
        </Alert>

        {/* Support Form Dialog */}
        <Dialog
          open={showForm}
          onClose={() => !loading && setShowForm(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '32px',
              p: 2,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
            <Typography variant="h3" fontWeight="900">
              {t('supportPage.form.title')} ✍️
            </Typography>
            {!loading && (
              <IconButton onClick={() => setShowForm(false)} sx={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>

          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <TextField
                fullWidth
                label={t('supportPage.form.subject')}
                value={supportForm.subject}
                onChange={(e) => setSupportForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={t('supportPage.form.subjectPlaceholder')}
                required
                InputProps={{
                  sx: { borderRadius: '16px', fontSize: '1.2rem', p: 1 }
                }}
                InputLabelProps={{
                  sx: { fontSize: '1.2rem' }
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('supportPage.form.message')}
                value={supportForm.message}
                onChange={(e) => setSupportForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder={t('supportPage.form.messagePlaceholder')}
                required
                InputProps={{
                  sx: { borderRadius: '20px', fontSize: '1.2rem', p: 1 }
                }}
                InputLabelProps={{
                  sx: { fontSize: '1.2rem' }
                }}
              />

              <Box sx={{ maxWidth: { xs: '100%', md: '60%' } }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.2rem' }}>{t('supportPage.form.priority')}</InputLabel>
                  <Select
                    value={supportForm.priority}
                    onChange={(e) => setSupportForm(prev => ({ ...prev, priority: e.target.value as any }))}
                    label={t('supportPage.form.priority')}
                    sx={{ borderRadius: '16px', fontSize: '1.2rem' }}
                  >
                    {priorityOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: option.color,
                              flexShrink: 0
                            }}
                          />
                          <Box>
                            <Typography variant="h6" fontWeight="900">{option.label}</Typography>
                            <Typography variant="body1" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Alert
                severity="info"
                sx={{
                  borderRadius: '20px',
                  backgroundColor: mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 246, 255, 1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}
              >
                <Typography variant="h6" fontWeight="900" sx={{ mb: 1 }}>
                  {t('supportPage.form.autoFilledTitle')}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                  • {t('supportPage.form.name')}: <strong>{user?.preferredName || user?.firstName} {user?.lastName ? user?.lastName.charAt(0) + '.' : ''}</strong><br />
                  • {t('supportPage.form.email')}: <strong>{user?.email}</strong><br />
                  • {t('supportPage.form.platform')}: <strong>{t('supportPage.form.platformValue')}</strong>
                </Typography>
              </Alert>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 4, gap: 2 }}>
            <Button
              onClick={() => setShowForm(false)}
              disabled={loading}
              sx={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'none',
                px: 4,
                borderRadius: '12px'
              }}
            >
              {t('supportPage.form.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitForm}
              disabled={loading || !supportForm.subject.trim() || !supportForm.message.trim()}
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
              sx={{
                py: 1.5,
                px: 5,
                borderRadius: '16px',
                fontSize: '1.3rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                textTransform: 'none',
                boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {loading ? t('supportPage.form.submitting') : t('supportPage.form.submit')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SupportPage;